































































































































import { Component, Prop, Vue } from 'vue-property-decorator';
import { BForm, BContainer, BRow, BCol, BButton, BIconCloudUpload, BIconTrash, BFormGroup, BFormInput, BFormInvalidFeedback, BFormSelect, BFormSelectOption, BFormTextarea, BFormCheckbox, BIconCalendar, BInputGroup, BInputGroupAppend, BOverlay, BTabs, BTab, BIconPlus } from 'bootstrap-vue';
import Configuration from '@/types/Configuration';
import Validator, { handleValidationErrors } from '@/types/Validator';
import EntityReference from '@/types/EntityReference';
import DateTimePicker from '@/components/DateTimePicker.vue';
import DateTime from '@/types/DateTime';
import TaskUserList from '@/components/TaskUserList.vue';
import AxiosService from '@/services/axiosService';
import i18n from '../i18n';

@Component({
    components: {
        'b-overlay': BOverlay,
        'b-form': BForm,
        'b-container': BContainer,
        'b-tabs': BTabs,
        'b-tab': BTab,
        'b-row': BRow,
        'b-col': BCol,
        'b-btn': BButton,
        'b-icon-cloud-upload': BIconCloudUpload,
        'b-icon-trash': BIconTrash,
        'b-form-group': BFormGroup,
        'b-input-group': BInputGroup,
        'b-input-group-append': BInputGroupAppend,
        'b-form-input': BFormInput,
        'b-form-invalid-feedback': BFormInvalidFeedback,
        'b-form-select': BFormSelect,
        'b-form-select-option': BFormSelectOption,
        'b-form-textarea': BFormTextarea,
        'b-form-checkbox': BFormCheckbox,
        'b-icon-calendar': BIconCalendar,
        'b-icon-plus': BIconPlus,
        'date-picker': DateTimePicker,
        'task-user-list': TaskUserList
    }
})
export default class TaskDetails extends Vue {
    @Prop() taskId!: string;

    apiAccessToken = this.$store.state.apiAccessToken;
    isBusy = true;

    task: TaskData = {
        id: '',
        sequenceId: 0,
        name: '',
        projectId: null,
        project: null,
        projectManager: null,
        startDate: null,
        endDate: null,
        identifier: '',
        description: '',
        isActive: true,
        users: []
    };
    projectManager: EntityReference | null = null;
    projectManagerName: string | null = null;
    startDate: string | null = null;
    endDate: string | null = null;

    lookupProjects: ProjectLookupItem[] = [];
    lookupUsers: EntityReference[] = [];
    user: EntityReference = {
        id: '',
        name: ''
    };

    nameValidator: Validator = {
        isValid: null,
        errors: []
    };
    projectValidator: Validator = {
        isValid: null,
        errors: []
    };
    startDateValidator: Validator = {
        isValid: null,
        errors: []
    };
    endDateValidator: Validator = {
        isValid: null,
        errors: []
    };
    identifierValidator: Validator = {
        isValid: null,
        errors: []
    };

    mounted() {
        this.apiAccessToken = this.$store.state.apiAccessToken;
        this.clearValidators();
        this.loadTask(this.taskId);
    }

    clearValidators() {
        this.nameValidator = {
            isValid: null,
            errors: []
        };
        this.projectValidator = {
            isValid: null,
            errors: []
        };
        this.startDateValidator = {
            isValid: null,
            errors: []
        };
        this.endDateValidator = {
            isValid: null,
            errors: []
        };
    }

    async loadProjects() {
        try {
            const response = await AxiosService.request<ProjectList>({
                url: `projectslookup`,
                method: 'GET'
            });

            this.lookupProjects = response.data.records ?? [];
        }
        catch (error) {
            if (error.response) {
                switch (error.response.status) {
                    case 404:
                        return this.$router.push(`/${i18n.locale}/notfound`);
                    case 403:
                        return this.$router.push(`/${i18n.locale}/accessdenied`);
                    default:
                        return this.$router.push(`/${i18n.locale}/error/${error.response.status}`);
                }
            }
        }
    }

    async loadUsers(projectId: string | null) {
        try {
            const response = await AxiosService.request<UserList>({
                url: `userslookup?projectId=${projectId}`,
                method: 'GET'
            });

            this.lookupUsers = response.data.records ?? [];
            if (this.task && this.projectManager) {
                if (this.lookupUsers.length == 0 || !this.lookupUsers.find(pm => pm.id == this.projectManager?.id)) {
                    this.lookupUsers.push(this.projectManager as EntityReference);
                }
            }
        }
        catch (error) {
            if (error.response) {
                switch (error.response.status) {
                    case 404:
                        return this.$router.push(`/${i18n.locale}/notfound`);
                    case 403:
                        return this.$router.push(`/${i18n.locale}/accessdenied`);
                    default:
                        return this.$router.push(`/${i18n.locale}/error/${error.response.status}`);
                }
            }
        }
    }

    async loadTask(taskId: string) {
        if (taskId.length == 0) {
            this.loadProjects();
            this.loadUsers(this.task.projectId);
            this.isBusy = false;
            return;
        }

        this.isBusy = true;

        DateTime.setLocale(this.$i18n.locale);

        try {
            const response = await AxiosService.request<TaskData>({
                url: `tasksadministration/${taskId}`,
                method: 'GET'
            });

            this.task = response.data;
            this.task.projectId = response.data.project?.id ?? '';
            this.initData(response.data.project, response.data.projectManager, this.task.startDate, this.task.endDate);
            this.loadProjects();
            this.loadUsers(this.task.projectId);
        }
        catch (error) {
            if (error.response) {
                switch (error.response.status) {
                    case 404:
                        return this.$router.push(`/${i18n.locale}/notfound`);
                    case 403:
                        return this.$router.push(`/${i18n.locale}/accessdenied`);
                    default:
                        return this.$router.push(`/${i18n.locale}/error/${error.response.status}`);
                }
            }
        }
        finally {
            this.isBusy = false;
        }
    }

    async onSubmit(e: Event) { // eslint-disable-line @typescript-eslint/no-unused-vars
        this.isBusy = true;

        this.clearValidators();

        try {
            await AxiosService.request<boolean>({
                url: `tasksadministration`,
                method: 'POST',
                data: this.task
            });

            this.$root.$bvToast.toast(this.$t('toaster.taskSaved') as string, {
                title: this.$t('toaster.saveTaskTitle') as string,
                autoHideDelay: Configuration.ToasterAutoHideDelay,
                appendToast: true,
                variant: Configuration.ToasterVariantSuccess,
                toaster: Configuration.ToasterPosition
            });
            this.$emit('task-saved', true);
        }
        catch (error) {
            if (error.response) {
                switch (error.response.status) {
                    case 404:
                        return this.$router.push(`/${i18n.locale}/notfound`);
                    case 403:
                        return this.$router.push(`/${i18n.locale}/accessdenied`);
                    case 400:
                        handleValidationErrors(this, error.response.data.errors.Name, this.nameValidator);
                        handleValidationErrors(this, error.response.data.errors.Project, this.projectValidator);
                        handleValidationErrors(this, error.response.data.errors.StartDate, this.startDateValidator);
                        handleValidationErrors(this, error.response.data.errors.EndDate, this.endDateValidator);
                        handleValidationErrors(this, error.response.data.errors.Identifier, this.identifierValidator);
                        return;
                    default:
                        return this.$router.push(`/${i18n.locale}/error/${error.response.status}`);
                }
            }
        }
        finally {
            this.isBusy = false;
        }
    }

    async deleteTask() {
        try {
            const response = await AxiosService.request({
                url: `tasksadministration/candelete/${this.taskId}`,
                method: 'GET'
            });

            const canDelete = response.data.canDelete;

            if (!canDelete) {
                this.$bvModal.msgBoxOk(
                    this.$t('modal.deniedTaskDeleteMessage', { taskName: this.task.name }) as string, {
                        title: this.$t('modal.deniedTaskDeleteTitle') as string,
                        centered: true,
                        buttonSize: 'sm',
                        okTitle: this.$t('modal.closeModal') as string
                    }
                );
                return;
            }

            this.$bvModal.msgBoxConfirm(this.$t('modal.confirmTaskDeleteMessage', { taskName: this.task.name }) as string, {
                title: this.$t('modal.confirmTaskDeleteTitle') as string,
                    centered: true,
                    buttonSize: 'sm',
                    okTitle: this.$t('modal.confirmOkTitle') as string,
                    cancelTitle: this.$t('modal.confirmCancelTitle') as string
                })
                .then(value => {
                    if (value) {
                        this.isBusy = true;

                        try {
                            AxiosService.request < boolean > ({
                                url: `tasksadministration?id=${this.taskId}`,
                                method: 'DELETE'
                            });

                            this.$root.$bvToast.toast(this.$t('toaster.taskDeleted', { taskName: this.task.name }) as string, {
                                title: this.$t('toaster.deleteTaskTitle') as string,
                                autoHideDelay: Configuration.ToasterAutoHideDelay,
                                appendToast: true,
                                variant: Configuration.ToasterVariantSuccess,
                                toaster: Configuration.ToasterPosition
                            });
                            this.$emit('task-deleted', true);
                        }
                        catch (error) {
                            if (error.response) {
                                switch (error.response.status) {
                                    case 404:
                                        return this.$router.push(`/${i18n.locale}/notfound`);
                                    case 403:
                                        return this.$router.push(`/${i18n.locale}/accessdenied`);
                                    default:
                                        return this.$router.push(`/${i18n.locale}/error/${error.response.status}`);
                                }
                            }
                        }
                        finally {
                            this.isBusy = false;
                        }
                    }
                })
                .catch(error => {
                    console.error(error);
                });
        }
        catch (error) {
            if (error.response) {
                switch (error.response.status) {
                    case 404:
                        return this.$router.push(`/${i18n.locale}/notfound`);
                    case 403:
                        return this.$router.push(`/${i18n.locale}/accessdenied`);
                    default:
                        return this.$router.push(`/${i18n.locale}/error/${error.response.status}`);
                }
            }
        }
        finally {
            this.isBusy = false;
        }
    }
    
    onProjectChange(projectId: string) {
        if (projectId) {
            const project = this.lookupProjects.find((p) => p.id === projectId);
            if (project) {
                this.initData(project, project.projectManager, project.startDate, project.endDate);
            }
            this.loadUsers(projectId);
        }
        else {
            this.initData(null, null, null, null);
        }
    }

    onChangeStartDate(e: string) {
        this.task.startDate = e;
    }

    onChangeEndDate(e: string) {
        this.task.endDate = e;
    }

    addUser() {
        const usr = this.task.users.filter(u => { return u.id == this.user.id });

        if (usr && usr.length > 0) {
            this.$root.$bvToast.toast(this.$t('toaster.addUserWarningUserExists') as string, {
                title: this.$t('toaster.addUserWarningTitle') as string,
                autoHideDelay: Configuration.ToasterAutoHideDelay,
                appendToast: true,
                variant: Configuration.ToasterVariantWarning,
                toaster: Configuration.ToasterPosition
            });
        }
        else {
            if (this.user.id) {
                this.task.users.push({
                    id: this.user.id,
                    name: this.user.name,
                })
            }
        }
    }

    initData(project: EntityReference | null, projectManager: EntityReference | null, startDate: string | null, endDate: string | null) {
        this.task.project = project;
        this.projectManager = projectManager;
        this.projectManagerName = (projectManager) ? projectManager.name : null;
        this.startDate = startDate;
        this.endDate = endDate;
    }
}

interface ProjectLookupItem extends EntityReference {
    projectManager: EntityReference;
    startDate: string;
    endDate: string;
}

interface TaskData {
    id: string;
    sequenceId: number;
    name: string;
    projectId: string | null;
    project: EntityReference | null;
    projectManager: EntityReference | null;
    startDate: string | null;
    endDate: string | null;
    identifier: string;
    isActive: boolean;
    description: string;
    users: EntityReference[];
}

interface ProjectList {
    records: ProjectLookupItem[];
}

interface UserList {
    records: EntityReference[];
}
